import { z } from 'zod';

/**
 * Filters and extracts parameters that match the specified schema.
 *
 * This function iterates over the provided parameters and retains only those
 * that are defined in the given schema. It is useful for validating and
 * sanitizing input data by ensuring that only expected parameters are processed.
 *
 * @param params - An object containing the parameters to be filtered.
 * @param schema - A Zod schema object that defines the valid structure and types of the parameters.
 * @returns An object containing only the parameters that conform to the schema.
 */
export function dropNonSchemaParams(params: Record<string, any>, schema: z.ZodObject<any>): Record<string, any> {
  const validParams: Record<string, any> = {};
  for (const key in params) {
    if (schema.shape[key]) {
      validParams[key] = params[key];
    }
  }
  return validParams;
}

// Reusable validation function for range checks
export const validateRange = (min: number, max: number) => (numVal: number | undefined) => {
  return numVal === undefined ? true : numVal >= min && numVal <= max;
};

// Reusable validation function for range checks with array
export const validateRangeArray =
  (min: number, max: number) => (numVal: number | (number | undefined)[] | undefined) => {
    if (numVal === undefined) {
      return true;
    }
    if (Array.isArray(numVal)) {
      return numVal.every((val) => val === undefined || (val >= min && val <= max));
    }
    return numVal >= min && numVal <= max;
  };

// Function to check for duplicate values in an array
export const hasNoDuplicates = (arr: any[]) => new Set(arr).size === arr.length;

// Reusable transformation function to remove default values
export const removeDefaultValue = (defaultValue: number) => (val: number | undefined) => {
  return val === defaultValue ? undefined : val;
};

// Reusable transformation function that returns an array or undefined.
export const toArray = (val: any) => {
  return val ? (Array.isArray(val) ? val.sort() : [val]) : undefined;
};
